import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  clearPendingAlipayRedirect,
  completePendingAlipayRedirect,
  getPendingAlipayRedirect,
  refreshPaymentIntent,
  type PaymentIntentRefresh,
} from '../lib/payment-methods';

export default function AlipayReturnPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentIntentId = searchParams.get('payment_intent_id')?.trim() ?? '';
  const verifiedReturn = useMemo(() => {
    const pending = getPendingAlipayRedirect();
    return pending?.paymentIntentId === paymentIntentId
      && pending.outcome?.kind === 'succeeded'
      ? pending
      : null;
  }, [paymentIntentId]);
  const [recordedReturnPath] = useState(verifiedReturn?.sourcePath ?? null);
  const [result, setResult] = useState<PaymentIntentRefresh | null>(
    verifiedReturn?.outcome?.kind === 'succeeded' ? verifiedReturn.outcome.result : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const returnPath = useMemo(
    () => recordedReturnPath ?? fallbackReturnPath(result),
    [recordedReturnPath, result],
  );

  useEffect(() => {
    if (verifiedReturn?.outcome?.kind === 'succeeded') {
      clearPendingAlipayRedirect(paymentIntentId);
      return;
    }
    if (!paymentIntentId) {
      setError('缺少支付订单号。');
      return;
    }

    let cancelled = false;
    const refresh = async () => {
      setRefreshing(true);
      try {
        const next = await refreshPaymentIntent(paymentIntentId);
        if (cancelled) return;
        if (next.paymentStatus === 'succeeded') {
          setResult(next);
          clearPendingAlipayRedirect(paymentIntentId);
          return;
        }

        navigate(
          completePendingAlipayRedirect(paymentIntentId, fallbackReturnPath(next)),
          { replace: true },
        );
      } catch {
        if (!cancelled) {
          navigate(
            completePendingAlipayRedirect(paymentIntentId, '/subscriptions'),
            { replace: true },
          );
        }
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    };

    void refresh();
    return () => {
      cancelled = true;
    };
  }, [navigate, paymentIntentId, verifiedReturn]);

  const succeeded = result?.paymentStatus === 'succeeded';
  const heading = succeeded ? '支付成功' : '正在确认支付宝支付';
  const description = succeeded
    ? '服务端已确认到账并处理购买内容。'
    : '支付宝返回页面不会直接判定到账，猴毛正在向服务端核实。';

  return (
    <div className="mx-auto max-w-lg py-12 text-center">
      <div className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
        <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full text-2xl ${
          succeeded ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
        }`}>
          {succeeded ? '✓' : '…'}
        </div>
        <h1 className="mt-5 text-2xl font-bold text-stone-950">{heading}</h1>
        <p className="mt-3 text-sm leading-6 text-stone-600">{description}</p>
        {refreshing ? <p className="mt-3 text-xs text-stone-500">正在刷新订单状态...</p> : null}
        {error ? <p className="mt-4 text-sm text-red-600" role="alert">{error}</p> : null}
        {(succeeded || error) ? (
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              className="inline-flex rounded-full border border-stone-300 px-6 py-3 text-sm font-semibold text-stone-800"
              href={returnPath}
              onClick={() => clearPendingAlipayRedirect(paymentIntentId)}
            >
              返回猴毛
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function fallbackReturnPath(result: PaymentIntentRefresh | null): string {
  if (result?.checkoutType === 'credit_recharge') return '/credits';
  if (result?.checkoutType === 'agent_access') {
    return result.profile
      ? `/chat?${new URLSearchParams({ agent_profile: result.profile }).toString()}`
      : '/chat';
  }
  return '/subscriptions';
}
